import usePolaris from 'src/hooks/usePolaris';
import { useCallback, useMemo } from 'react';

import useAppContext from 'src/hooks/useAppContext';
import { v4 as uuid } from 'uuid';
import InterviewPreviewCard from 'src/HomePage/components/InterviewPreviewCard';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import { trackRelatedQuestionClicked, trackRelatedQuestionViewed } from '../../../../analytics';
import useInterviewType from 'src/interviews/hooks/useInterviewType';

import type { RelatedQuestionType } from '../../hooks/useRelatedQuestions';

const RelatedQuestion = ({ question, index }: { question: RelatedQuestionType; index: number }) => {
  const { send, addInterview, state } = usePolaris();
  const subActors = state?.context?.subActors;

  const { setIsPopUpOpen, interviewSettings } = useAppContext();

  const impressionId = useMemo(() => uuid(), []);
  const inProgressSiteApiInterviews = useMemo(
    () =>
      Object.values(subActors).filter(
        (subActor: any) =>
          ['dxoc', 'emergency', 'symptoms'].includes(subActor?.state?.context?.interviewType) &&
          subActor?.state?.context?.chatList?.active > 0 &&
          !subActor?.state?.context?.completion.results,
      ),
    [subActors],
  );

  const pxocInterviewFromConfig = useMemo(
    () =>
      (question.interview_type === 'pxoc' &&
        interviewSettings?.find((setting) => setting.interview === 'pxoc')) ||
      false,
    [question.interview_type, interviewSettings],
  );

  const isSiteApiRelatedQuestion = ['dxoc', 'emergency', 'symptoms'].includes(
    question.interview_type,
  );
  const hide = isSiteApiRelatedQuestion && inProgressSiteApiInterviews.length > 0;

  const sourceInterviewType = useInterviewType();

  const impressionRef = useImpressionTracking(() => {
    trackRelatedQuestionViewed({
      impressionId,
      order: index,
      sourceInterviewType: sourceInterviewType,
      destinationInterviewType: question.interview_type,
    });
  });

  const interviewConfig = useMemo(() => {
    // For pxoc, try to get qtext from config. Otherwise, use default hardcoded qtext from api
    const questionText =
      question.interview_type === 'pxoc' && pxocInterviewFromConfig
        ? pxocInterviewFromConfig.question || question.question
        : question.question;

    const existingResultsToken = state?.context.resultsToken;

    // RQ is an interview hop from a predictor token in the results payload.
    // - e.g. symptoms interview hop into a dxoc interview
    const isDirectInterviewHop = question.chief_complaint || question.dxoc;

    // RQ is an interview hop from a predictor id and not a token. This occurs when user
    // is on a non SiteAPI interview and can hop into a SiteAPI interview. Since there is no
    // results payload for non SiteAPI interviews, potential site API RQs are dervied from sanity
    // - e.g. symptoms interview -> TG -> dxoc
    const isIndirectInterviewHop = existingResultsToken && (question.complaints || question.dxocs);
    return {
      configs: [
        {
          context: {
            chiefComplaint: question.chief_complaint || '',
            ddxConsidered: question.dxoc || '',
            triageRuleConsidered: question.triage_rule_considered || '',
            complaints: question.complaints || [],
            conditionName: question.condition_name || '',
            dxocs: question.dxocs || undefined,
            isInterviewHop: Boolean(isDirectInterviewHop || isIndirectInterviewHop),
            questions: question.questions || [],
            resultsToken: existingResultsToken || '',
            slug: question.slug || '',
          },
          id: `${question.slug || question.condition_name}-${question.interview_type}-${question.interview_type}`,
          interviewType: question.interview_type,
          parentInterviewId: `${question.slug || question.condition_name}-${question.interview_type}`,
        },
      ],
      id: `${question.slug || question.condition_name}-${question.interview_type}`,
      question: questionText,
      interview: question.interview_type,
    };
  }, [pxocInterviewFromConfig, question, state?.context.resultsToken]);

  const handleInterviewClick = useCallback(
    (id: string) => {
      if (!interviewConfig) {
        return;
      }

      trackRelatedQuestionClicked({
        impressionId,
        sourceInterviewType: sourceInterviewType,
        destinationInterviewType: question.interview_type,
      });
      addInterview({
        ...interviewConfig,
        configs: interviewConfig.configs.map((c) => ({
          ...c,
          context: {
            ...c.context,
            polarisProps: {
              displayPolarisUI: true,
              showProgressBar: true,
            },
          },
        })),
      });

      if (!state?.context?.currentInterview) {
        send?.('SUBMIT', { id });
      } else {
        send?.('TRANSITION', { id });
      }
      setIsPopUpOpen?.(true);
    },
    [
      addInterview,
      impressionId,
      interviewConfig,
      question.interview_type,
      send,
      setIsPopUpOpen,
      sourceInterviewType,
      state?.context?.currentInterview,
    ],
  );

  return (
    <div ref={impressionRef}>
      {!hide && interviewConfig && (
        <InterviewPreviewCard
          interview={interviewConfig}
          onClick={handleInterviewClick}
          shouldShowPreview={false}
        />
      )}
    </div>
  );
};

export default RelatedQuestion;
